import { isRedoEmailAddress } from "@redotech/account-common/redo-domains";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { getScheduleDemoDestinationUrl } from "@redotech/redo-conversion-booking-common/schedule-demo-url";
import { RedoConversionRpcClientContext } from "@redotech/redo-merchant-app-common/redo-conversion-rpc-client-provider";
import { UserContext } from "@redotech/redo-merchant-app-common/user";
import { ScheduleDemoButtonLocation } from "@redotech/redo-merchant-conversion-rpc/schema/analytics/send-schedule-demo-clicked-notification";
import { createContext, memo, useContext, useMemo, useState } from "react";
import { OrderCountsModal } from "../welcome/order-counts-modal";
import { OrderCountsModalData } from "../welcome/use-handle-schedule-demo";

interface ScheduleDemoContextValue {
  /**
   * For Redo employees, opens the order-counts modal instead of notifying the
   * CRO squad / opening HubSpot. For everyone else, sends the schedule-demo
   * notification and opens the HubSpot meeting link.
   */
  handleScheduleDemo: (args: {
    buttonLocation: ScheduleDemoButtonLocation;
  }) => Promise<void>;
  scheduleDemoPending: boolean;
}

const ScheduleDemoContext = createContext<ScheduleDemoContextValue | undefined>(
  undefined,
);

/**
 * Suite-wide host for the schedule-demo flow. Mounted once near the conversion
 * suite root so every surface that offers a "schedule a demo / call" action
 * (welcome page, dashboard, locked sidebar nav items, onboarding banners) gets
 * the same Redo-employee guard and shares a single order-counts modal.
 */
export const ScheduleDemoProvider = memo(function ScheduleDemoProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const conversionRpcClient = useRequiredContext(
    RedoConversionRpcClientContext,
  );
  const user = useContext(UserContext);

  const [scheduleDemoPending, setScheduleDemoPending] = useState(false);
  const [orderCountsModal, setOrderCountsModal] = useState<
    OrderCountsModalData | undefined
  >(undefined);

  const handleScheduleDemo = useHandler(
    async ({
      buttonLocation,
    }: {
      buttonLocation: ScheduleDemoButtonLocation;
    }) => {
      setScheduleDemoPending(true);
      try {
        if (isRedoEmailAddress(user?.email)) {
          const orderCounts =
            await conversionRpcClient.getOnlineStoreOrderCounts();
          setOrderCountsModal(
            orderCounts.success
              ? {
                  lastThirtyDaysCount: orderCounts.lastThirtyDaysCount,
                  lastYearCount: orderCounts.lastYearCount,
                }
              : { lastThirtyDaysCount: 0, lastYearCount: 0 },
          );
          return;
        }
        const scheduleDemoDestinationUrl = getScheduleDemoDestinationUrl();
        await conversionRpcClient.sendScheduleDemoClickedNotification({
          source: buttonLocation,
          calendarUrl: scheduleDemoDestinationUrl,
        });
        window.open(scheduleDemoDestinationUrl, "_blank");
      } finally {
        setScheduleDemoPending(false);
      }
    },
  );

  const closeOrderCountsModal = useHandler(() => {
    setOrderCountsModal(undefined);
  });

  const value = useMemo(
    () => ({ handleScheduleDemo, scheduleDemoPending }),
    [handleScheduleDemo, scheduleDemoPending],
  );

  return (
    <ScheduleDemoContext.Provider value={value}>
      {orderCountsModal && (
        <OrderCountsModal
          data={orderCountsModal}
          onClose={closeOrderCountsModal}
        />
      )}
      {children}
    </ScheduleDemoContext.Provider>
  );
});

export function useScheduleDemo(): ScheduleDemoContextValue {
  return useRequiredContext(ScheduleDemoContext);
}
